import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ngeohash from 'ngeohash';
import IconPicker from './IconPicker';
import GeohashPreviewMap from './GeohashPreviewMap';
import AppTypesField, { AppTypesBadges } from './AppTypesField';
import AdBannerPreview from './AdBannerPreview';
import AdImageFields from './AdImageFields';
import styles from './Advertising.module.css';
import useAuthStore from '../stores/useAuthStore';
import * as playgroundService from '../services/playgroundService';
import {
  normalizeAdGeohashes,
  normalizeAdAppTypes,
  buildAdvertisingSavePayload,
  getAdvertisingSaveErrorMessage,
  getGeohashRadiusMiles,
} from '../utils/advertisingUtils';

function GeohashRows({ geohashes, onChange, lat, lon }) {
  const rows = geohashes.length > 0 ? geohashes : [''];

  const setRow = (index, val) => {
    const next = [...rows];
    next[index] = val;
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, '']);
  };

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length ? next : ['']);
  };

  const suggested = lat && lon ? ngeohash.encode(parseFloat(lat), parseFloat(lon), 4) : null;

  const appendSuggested = () => {
    if (!suggested) return;
    if (rows.some((g) => String(g).trim() === suggested)) {
      toast.info('That prefix is already in the list');
      return;
    }
    const trimmed = rows.map((g) => String(g).trim());
    const next = [...trimmed.filter(Boolean), suggested];
    onChange(next.length ? next : [suggested]);
  };

  return (
    <div>
      <label className={styles.label}>Geohash prefixes</label>
      {rows.map((g, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={g}
            onChange={(e) => setRow(index, e.target.value)}
            placeholder="e.g. dr5"
            className={styles.field}
            style={{ flex: 1, width: 'auto', minWidth: 0 }}
          />
          {getGeohashRadiusMiles(String(g).trim()) && (
            <span className={styles.hint} style={{ whiteSpace: 'nowrap' }}>
              ~{getGeohashRadiusMiles(String(g).trim())} mi
            </span>
          )}
          <button type="button" className={styles.ghostBtn} onClick={() => removeRow(index)}>
            Remove
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <button type="button" className={styles.ghostBtn} onClick={addRow}>
          Add prefix
        </button>
        {suggested && (
          <button type="button" className={styles.ghostBtn} onClick={appendSuggested}>
            Add suggested 4-char ({suggested})
          </button>
        )}
      </div>
    </div>
  );
}

function AdCard({ ad, onOpen }) {
  const [theme, setTheme] = React.useState('light');
  const mapsUrl = ad.lat && ad.lon ? `https://www.google.com/maps?q=${ad.lat},${ad.lon}` : null;
  const ghList = normalizeAdGeohashes(ad);
  const radius = ghList[0] ? getGeohashRadiusMiles(ghList[0]) : null;
  const isActive = ad.active !== false;

  return (
    <div
      className={`${styles.card} ${isActive ? styles.cardActive : styles.cardInactive}`}
      onClick={onOpen}
    >
      <AdBannerPreview ad={ad} theme={theme} onThemeChange={setTheme} />
      <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <h3 className={styles.cardTitle}>{ad.name || ad.headline}</h3>
          <span style={{ fontSize: 11, color: isActive ? 'var(--color-success, #4caf50)' : 'var(--color-text-muted)', fontWeight: 600 }}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <AppTypesBadges ad={ad} />
        <div className={styles.muted}>
          Geohash{ghList.length !== 1 ? 'es' : ''}: {ghList[0] || '—'}
          {ghList.length > 1 && <span style={{ marginLeft: 6, fontWeight: 600 }}>+{ghList.length - 1} more</span>}
        </div>
        <div className={styles.muted}>Sold By: {ad.soldBy || '—'}</div>
        <div className={styles.muted}>
          Start: {ad.startDate} | End: {ad.endDate}
        </div>
        {radius && ghList.length === 1 && <div className={styles.muted}>Cell radius: ~{radius} miles</div>}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`${styles.muted} ${styles.link}`}
          >
            View on Google Maps
          </a>
        )}
        {ad.url && (
          <div className={styles.link} style={{ fontSize: 10, marginTop: 1, wordBreak: 'break-all' }}>
            {ad.url}
          </div>
        )}
      </div>
    </div>
  );
}

// Advertising List Component
export function Advertising() {
  const [ads, setAds] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    async function fetchAds() {
      try {
        setLoading(true);
        setError(null);
        const items = await playgroundService.fetchAdvertising();
        setAds(items);
      } catch {
        setError('Failed to load advertising data');
      } finally {
        setLoading(false);
      }
    }
    fetchAds();
  }, []);

  if (loading) return <div>Loading advertising...</div>;
  if (error) return <div>{error}</div>;
  if (!ads.length) return <div>No advertising found.</div>;

  return (
    <div>
      <h2>Advertising</h2>
      <button className={styles.primaryBtn} style={{ marginBottom: 16 }} onClick={() => navigate('/advertising/edit/new')}>
        Add New
      </button>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px',
          justifyItems: 'center',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: 1200,
          margin: '0 auto',
          padding: '8px',
        }}
      >
        {ads.map((ad) => (
          <AdCard key={ad.id} ad={ad} onOpen={() => navigate(`/advertising/edit/${ad.id}`)} />
        ))}
      </div>
    </div>
  );
}

// Edit Advertising Component
export function EditAdvertising() {
  const { id } = useParams();
  const navigate = useNavigate();
  const getAuthHeaders = useAuthStore((s) => s.getAuthHeaders);
  const [ad, setAd] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);
  const [uploadingField, setUploadingField] = React.useState(null);
  const [previewTheme, setPreviewTheme] = React.useState('light');

  React.useEffect(() => {
    async function fetchAd() {
      try {
        setLoading(true);
        setError(null);
        let found;
        if (id === 'new') {
          found = {
            id: `ad-${Date.now()}`,
            name: '',
            headline: '',
            tagline: '',
            imageUrl: '',
            imageUrlDark: '',
            wideBannerImageUrl: '',
            wideBannerImageUrlDark: '',
            imageFit: '',
            startDate: '',
            endDate: '',
            lat: '',
            lon: '',
            url: '',
            geohashes: [''],
            soldBy: '',
            active: true,
            icon: {},
            appTypes: [],
          };
        } else {
          const items = await playgroundService.fetchAdvertising();
          found = items.find((item) => item.id === id);
          if (found) {
            const gh = normalizeAdGeohashes(found);
            found = {
              ...found,
              geohashes: gh.length ? gh : [''],
              tagline: found.tagline || found.message || '',
              imageUrlDark: found.imageUrlDark || '',
              wideBannerImageUrl: found.wideBannerImageUrl || '',
              wideBannerImageUrlDark: found.wideBannerImageUrlDark || '',
              icon: found.icon && typeof found.icon === 'object' ? found.icon : {},
              appTypes: normalizeAdAppTypes(found),
            };
          }
        }
        setAd(found);
      } catch {
        setError('Failed to load ad');
      } finally {
        setLoading(false);
      }
    }
    fetchAd();
  }, [id]);

  const handleChange = (field, value) => {
    setAd((prev) => ({ ...prev, [field]: value }));
  };

  const bannerFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Could not read file'));
          return;
        }
        const i = result.indexOf(',');
        resolve({ base64: i >= 0 ? result.slice(i + 1) : result, contentType: file.type || 'image/jpeg' });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    setUploadingField(field);
    try {
      const { base64, contentType } = await bannerFileToBase64(file);
      const authHeaders = await getAuthHeaders();
      const kind = field.startsWith('wideBanner') ? 'wideBanner' : 'ad';
      const res = await playgroundService.uploadPublicAsset(
        { fileContent: base64, contentType, kind, imageFit: ad.imageFit || undefined },
        authHeaders
      );
      if (res?.url) {
        handleChange(field, res.url);
        toast.success('Image uploaded');
      } else {
        toast.error('Upload did not return a URL');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingField(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildAdvertisingSavePayload(ad);
      if (payload.imageFit === '') {
        delete payload.imageFit;
      }
      await playgroundService.saveAdvertising(payload);
      setSaving(false);
      navigate('/advertising');
    } catch (err) {
      setSaveError(getAdvertisingSaveErrorMessage(err));
      setSaving(false);
    }
  };

  if (loading) return <div>Loading ad...</div>;
  if (error) return <div>{error}</div>;
  if (!ad) return <div>Ad not found.</div>;

  const mapsUrl = ad.lat && ad.lon ? `https://www.google.com/maps?q=${ad.lat},${ad.lon}` : null;
  const ghList = normalizeAdGeohashes({ ...ad, geohashes: ad.geohashes });

  return (
    <div className={styles.editor}>
      <h2 className={styles.editorTitle}>{id === 'new' ? 'Add Advertising' : 'Edit Advertising'}</h2>
      <div style={{ marginBottom: 20 }}>
        <div className={styles.hint}>In-app preview</div>
        <div className={styles.previewFrame}>
          <AdBannerPreview ad={ad} theme={previewTheme} onThemeChange={setPreviewTheme} />
        </div>
      </div>
      {mapsUrl && (
        <div style={{ marginBottom: 16, textAlign: 'center' }}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles.link} style={{ fontWeight: 500, fontSize: 14 }}>
            View on Google Maps
          </a>
        </div>
      )}
      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Name</label>
              <input
                type="text"
                className={styles.field}
                value={ad.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Headline</label>
              <input
                type="text"
                className={styles.field}
                value={ad.headline || ''}
                onChange={(e) => handleChange('headline', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={styles.label}>
              Tagline (saved as <code>message</code> for API consumers)
            </label>
            <input
              type="text"
              className={styles.field}
              value={ad.tagline || ''}
              onChange={(e) => handleChange('tagline', e.target.value)}
            />
          </div>
          <AdImageFields ad={ad} uploadingField={uploadingField} onChange={handleChange} onUpload={handleImageUpload} />
          <div>
            <label className={styles.label}>Sponsor icon (optional)</label>
            <IconPicker
              kind="ad"
              showAnchor={false}
              showImageFit
              value={ad.icon || {}}
              onChange={(icon) => handleChange('icon', icon)}
            />
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Start Date</label>
              <input
                type="date"
                value={ad.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className={styles.field}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>End Date</label>
              <input
                type="date"
                value={ad.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className={styles.field}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Latitude</label>
              <input
                type="number"
                value={ad.lat || ''}
                onChange={(e) => handleChange('lat', e.target.value)}
                className={styles.field}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Longitude</label>
              <input
                type="number"
                value={ad.lon || ''}
                onChange={(e) => handleChange('lon', e.target.value)}
                className={styles.field}
              />
            </div>
          </div>
          <div>
            <label className={styles.label}>URL</label>
            <input
              type="text"
              value={ad.url || ''}
              onChange={(e) => handleChange('url', e.target.value)}
              className={styles.field}
            />
          </div>
          <AppTypesField value={ad.appTypes} onChange={(next) => handleChange('appTypes', next)} />
          <GeohashRows
            geohashes={ad.geohashes || ['']}
            lat={ad.lat}
            lon={ad.lon}
            onChange={(next) => handleChange('geohashes', next)}
          />
          <GeohashPreviewMap
            geohashes={ghList}
            adCenter={
              ad.lat !== '' &&
              ad.lon !== '' &&
              Number.isFinite(parseFloat(ad.lat)) &&
              Number.isFinite(parseFloat(ad.lon))
                ? { lat: parseFloat(ad.lat, 10), lon: parseFloat(ad.lon, 10) }
                : null
            }
          />
          {ghList.length > 0 && ghList[0] && (
            <div className={styles.link} style={{ marginBottom: 12, fontSize: 14, fontWeight: 500 }}>
              Primary cell radius: ~{getGeohashRadiusMiles(ghList[0])} miles
            </div>
          )}
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label className={styles.label}>Sold By</label>
              <input
                type="text"
                value={ad.soldBy || ''}
                onChange={(e) => handleChange('soldBy', e.target.value)}
                className={styles.field}
              />
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}>
              <label className={styles.label} style={{ marginBottom: 0 }}>Active</label>
              <input
                type="checkbox"
                checked={!!ad.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                style={{ width: 20, height: 20 }}
              />
            </div>
          </div>
        </div>
        <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button type="submit" className={styles.primaryBtn} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/advertising')}>
            Cancel
          </button>
        </div>
        {saveError && <div style={{ color: 'red', marginTop: 12, textAlign: 'center' }}>{saveError}</div>}
      </form>
    </div>
  );
}
